/* eslint-disable max-len */
import {
    Box, useBreakpointValue, useToken,
    VStack,
} from '@chakra-ui/react';
import { ComputedDatum, MayHaveLabel, ResponsivePie } from '@nivo/pie';
import { useMemo } from 'react';
import { FaIcon, GraphLegend, NonIdealState } from '@acp/core-ui';
import { faExclamationCircle } from '@fortawesome/pro-light-svg-icons';
import { useNavigate } from 'react-router-dom';
import { IntegratedPortfolioFacets } from '@acp/shared';
import { useGetNonManagedStats } from '../../../hooks/use-get-stats';
import { PortfolioGraphSkeleton } from '../graph-skeleton';

export function NonManagedPortfolioGraph() {
    const navigate = useNavigate();
    const compliantPieColor = useToken('colors', 'success.300');
    const compliantIssuesPieColor = useToken('colors', 'danger.400');
    const fontSizeSm = useToken('fontSizes', 'sm');
    const fontSizeXs = useToken('fontSizes', 'xs');
    const pieMargin = useBreakpointValue({
        base: {
            top: -60, right: 60, bottom: 30, left: 60,
        },
        sm: {
            top: 80, right: 80, bottom: 100, left: 80,
        },
    });
    const pieBorderWidth = useBreakpointValue({
        base: 1,
        md: 2,
    });
    const enableArcLinkLabels = useBreakpointValue({
        base: false,
        md: true,
    });
    const enableArcLabels = useBreakpointValue({
        base: true,
        md: false,
    });

    const { isLoading, data } = useGetNonManagedStats();

    const pieDatum = useMemo(() => {
        if (isLoading || data === undefined) return [];

        const pieData: (MayHaveLabel & { id: string; color: string; value: number })[] = [];

        // The order is important here, Nivo will render these in the order we give it
        // the data points, we want to render the smallest possible first and last so that the
        // tendrils end up on the left/right side of the top,
        // so there's less chance of them overlapping
        const { COMPLIANCE_ISSUES, COMPLIANT } = data.nonManagedPortfolioStats;

        if (COMPLIANT > 0) {
            pieData.push({
                id: 'Compliant*',
                label: 'Compliant*',
                value: COMPLIANT,
                color: compliantPieColor,
            });
        }

        if (COMPLIANCE_ISSUES > 0) {
            pieData.push({
                id: 'Compliance issues*',
                label: 'Compliance issues*',
                value: COMPLIANCE_ISSUES,
                color: compliantIssuesPieColor,
            });
        }

        return pieData;
    }, [isLoading, data]);

    if (isLoading || data === undefined) {
        return <PortfolioGraphSkeleton />;
    }

    const getPercentage = (value: number) => {
        const { COMPLIANCE_ISSUES, COMPLIANT } = data.nonManagedPortfolioStats;
        return ((value / (COMPLIANCE_ISSUES + COMPLIANT)) * 100).toFixed(1);
    };

    const handleClickSlice = (datum: ComputedDatum<MayHaveLabel & { id: string; color: string; value: number }>) => {
        const { id } = datum;
        const searchParams = new URLSearchParams();

        if (id === 'Compliant*') {
            searchParams.append('facet', IntegratedPortfolioFacets.Compliant);
        }

        if (id === 'Compliance issues*') {
            searchParams.append('facet', IntegratedPortfolioFacets.NonCompliant);
        }

        navigate(`/portfolio?managed-by=OTHER&${searchParams.toString()}`, {
            replace: true,
        });
    };

    if (pieDatum.length === 0) {
        return (
            <NonIdealState
                iconComponent={<FaIcon icon={faExclamationCircle} size="9x" color="muted.200" />}
                title="There are no properties on service with other providers. If this is an error please contact your account manager"
                py={24}
            />
        );
    }

    return (
        <VStack w="full">
            <Box w="99%" h={96} className="svg-overflow-visible">
                <ResponsivePie
                    data={pieDatum}
                    margin={pieMargin}
                    colors={(d) => d.data.color}
                    theme={{
                        text: {
                            fontFamily: "'Roboto'",
                        },
                        labels: {
                            text: {
                                fontSize: fontSizeSm,
                            },
                        },
                        legends: {
                            text: {
                                fontSize: fontSizeXs,
                            },
                        },
                        tooltip: {
                            container: {
                                fontSize: fontSizeSm,
                            },
                            chip: {
                                borderRadius: '100px',
                            },
                        },
                    }}
                    // startAngle={-160}
                    activeOuterRadiusOffset={8}
                    borderWidth={pieBorderWidth}
                    borderColor="white"
                    valueFormat={(value) => `${new Intl.NumberFormat('en-AU').format(value)} properties`}
                    enableArcLabels={enableArcLabels}
                    enableArcLinkLabels={enableArcLinkLabels}
                    arcLinkLabel={(d) => `${d.id} (${getPercentage(d.value)}%)`}
                    arcLinkLabelsSkipAngle={0}
                    arcLinkLabelsTextColor="#000"
                    arcLinkLabelsThickness={1}
                    arcLinkLabelsStraightLength={12}
                    arcLinkLabelsColor={{ from: 'color' }}
                    arcLabelsSkipAngle={15}
                    arcLabelsTextColor={{
                        from: 'color',
                        modifiers: [
                            [
                                'darker',
                                2,
                            ],
                        ],
                    }}
                    onClick={handleClickSlice}
                    onMouseEnter={(_datum, event) => {
                    // Not great, works perfectly
                    // https://github.com/plouc/nivo/issues/2276#issuecomment-1496478882
                    // eslint-disable-next-line no-param-reassign
                        event.currentTarget.style.cursor = 'pointer';
                    }}
                />
            </Box>
            <GraphLegend data={pieDatum.map((pieData) => ({
                label: pieData.label,
                legendIconColor: pieData.color,
            }))}
            />
        </VStack>
    );
}
